'use client'

import { useState, useCallback, useEffect, useMemo } from 'react'
import { t } from '@lingui/core/macro'
import { useGetActiveFormTemplatesQuery, useCreateFormSubmissionMutation, useGetMyInfosQuery } from '@/core/graphql/hooks'
import { FormField as FormFieldType, FormSubmissionStatus } from '@/core/graphql/types'

type FormDataRecord = Record<string, string | string[]>

export function useEthicsForm(patientId: string) {
  const [formData, setFormData] = useState<FormDataRecord>({})
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [formResetKey, setFormResetKey] = useState(0)

  const { data: templatesData, loading: isLoadingTemplates, error: templatesError } = useGetActiveFormTemplatesQuery()
  const [createSubmission, { loading: isSubmitting }] = useCreateFormSubmissionMutation()
  const { refetch } = useGetMyInfosQuery()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')

  const ethicsTemplates = useMemo(
    () => templatesData?.activeFormTemplates?.filter((tpl) => tpl.categories?.includes('ethics')) ?? [],
    [templatesData?.activeFormTemplates],
  )
  const ethicsTemplate = ethicsTemplates.find((tpl) => tpl._id === selectedTemplateId) ?? ethicsTemplates[0]

  useEffect(() => {
    if (ethicsTemplates.length === 0) {
      setSelectedTemplateId('')
      return
    }
    if (!selectedTemplateId || !ethicsTemplates.some((tpl) => tpl._id === selectedTemplateId)) {
      setSelectedTemplateId(ethicsTemplates[0]._id)
    }
  }, [ethicsTemplates, selectedTemplateId])

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId)
    setFormData({})
    setValidationErrors({})
    setSubmitSuccess(false)
    setFormResetKey((prev) => prev + 1)
  }

  const handleFieldChange = (fieldId: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }))
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => { const next = { ...prev }; delete next[fieldId]; return next })
    }
  }

  const handleSubmit = useCallback(async () => {
    if (!ethicsTemplate) return

    const errors: Record<string, string> = {}
    ethicsTemplate.fields.forEach((field: FormFieldType) => {
      const val = formData[field.id]
      if (field.required && (!val || (Array.isArray(val) && val.length === 0))) {
        errors[field.id] = t`Required`
      }
    })
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }
    setValidationErrors({})

    try {
      await createSubmission({
        variables: {
          payload: {
            template: ethicsTemplate._id,
            patient: patientId,
            responses: Object.entries(formData).map(([fieldId, value]) => ({ fieldId, value })),
            status: FormSubmissionStatus.Submitted,
            tags: ['ethics'],
          },
        },
      })
      setSubmitSuccess(true)
      setFormData({})
      setFormResetKey((prev) => prev + 1)
      await refetch()
      setTimeout(() => setSubmitSuccess(false), 5000)
    } catch {
      setValidationErrors({ _form: t`An error occurred while submitting your question.` })
    }
  }, [ethicsTemplate, patientId, formData, createSubmission, refetch])

  return {
    ethicsTemplate,
    ethicsTemplates,
    selectedTemplateId: ethicsTemplate?._id ?? selectedTemplateId,
    isLoadingTemplates,
    templatesError,
    formData,
    formResetKey,
    validationErrors,
    submitSuccess,
    isSubmitting,
    handleTemplateChange,
    handleFieldChange,
    handleSubmit,
  }
}
